// src/services/limitsService.ts
export interface UserLimits {
  maxLinks: number;
  maxWidgets: number;
  maxColumns: number;
  maxScratchpadLength: number;
  maxTagsPerLink: number;
  canRemoveAds: boolean;
  canExportData: boolean;
  canUseCustomWidgets: boolean;
}

export type SubscriptionTier = "free" | "pro" | "admin";

const LIMITS_CONFIG: Record<SubscriptionTier, UserLimits> = {
  free: {
    maxLinks: 50,
    maxWidgets: 5,
    maxColumns: 3,
    maxScratchpadLength: 5000, // 5k characters
    maxTagsPerLink: 3,
    canRemoveAds: false,
    canExportData: false,
    canUseCustomWidgets: false,
  },
  pro: {
    maxLinks: 500,
    maxWidgets: 20,
    maxColumns: 5,
    maxScratchpadLength: 50000, // 50k characters
    maxTagsPerLink: 10,
    canRemoveAds: true,
    canExportData: true,
    canUseCustomWidgets: true,
  },
  admin: {
    maxLinks: Infinity,
    maxWidgets: Infinity,
    maxColumns: 10,
    maxScratchpadLength: Infinity,
    maxTagsPerLink: Infinity,
    canRemoveAds: true,
    canExportData: true,
    canUseCustomWidgets: true,
  },
};

export const limitsService = {
  /**
   * Get limits for a specific subscription tier
   */
  getLimitsForTier(tier: SubscriptionTier): UserLimits {
    return LIMITS_CONFIG[tier];
  },

  /**
   * The signed-in user's admin status, set by the auth gate (useAuth) when it
   * resolves and cleared on sign-out. Every check below reads it.
   */
  _isAdmin: false,

  setAdminStatus(isAdmin: boolean) {
    this._isAdmin = isAdmin;
  },

  getUserTier(): SubscriptionTier {
    if (this._isAdmin) {
      return "admin";
    }

    // TODO: Check for pro subscription in user document
    return "free";
  },

  /**
   * Get current user's limits
   */
  getUserLimits(): UserLimits {
    const tier = this.getUserTier();
    return this.getLimitsForTier(tier);
  },

  /**
   * Check if user can perform an action based on current usage
   */
  canAddLink(currentLinkCount: number): boolean {
    const limits = this.getUserLimits();
    return currentLinkCount < limits.maxLinks;
  },

  canAddWidget(currentWidgetCount: number): boolean {
    const limits = this.getUserLimits();
    return currentWidgetCount < limits.maxWidgets;
  },

  canSetColumns(columnCount: number): boolean {
    const limits = this.getUserLimits();
    return columnCount <= limits.maxColumns;
  },

  canUseScratchpadLength(textLength: number): boolean {
    const limits = this.getUserLimits();
    return textLength <= limits.maxScratchpadLength;
  },

  /**
   * Get limit violation message for display to user
   */
  getLimitMessage(limitType: keyof UserLimits): string {
    const limits = this.getUserLimits();
    const tier = this.getUserTier();
    const limit = limits[limitType];

    if (tier === "free") {
      switch (limitType) {
        case "maxLinks":
          return `Free accounts are limited to ${limit} links. Upgrade to Pro for up to 500 links.`;
        case "maxWidgets":
          return `Free accounts are limited to ${limit} widgets. Upgrade to Pro for up to 20 widgets.`;
        case "maxColumns":
          return `Free accounts are limited to ${limit} columns. Upgrade to Pro for up to 5 columns.`;
        case "maxScratchpadLength":
          return `Free accounts are limited to ${(limit as number).toLocaleString()} characters in scratchpad. Upgrade to Pro for 50,000 characters.`;
        default:
          return `This feature requires a Pro subscription.`;
      }
    }

    return `You've reached the limit for your ${tier} account.`;
  },
};
